/**
 * Append server-derived activity rows when a snapshot is accepted (same pattern as former useRoundBubbleFeed).
 */

import type { RoundSnapshot } from "@/shared/api/types/pvpRoulette"
import { getActiveBets } from "@/shared/api/types/pvpRoulette"
import { roundDiff } from "./roundDiff"
import {
  deriveBubbleEventsFromDiff,
  type ActivityFeedItem,
} from "./deriveBubbleEventsFromDiff"
import type { RoomSlice } from "./types"

const ACTIVITY_FEED_MAX = 50

function waitingIntroItem(next: RoundSnapshot): ActivityFeedItem | null {
  if (next.status !== "WAITING") return null
  if (getActiveBets(next.bets).length > 0) return null
  return {
    id: `round:${next.roundId}:waiting-intro`,
    payload: {
      kind: "waiting_intro",
      hasSquadOption: (next.allowedTeams?.length ?? 0) > 0,
      hasTricks: (next.allowedTricks?.length ?? 0) > 0,
    },
  }
}

/**
 * @param prevSlice — room slice before reduceRoomState
 * @param reducedSlice — slice returned from reduceRoomState (carries prior `activityFeed` until overwritten here)
 */
export function mergeRoomActivityFeed(
  prevSlice: RoomSlice,
  reducedSlice: RoomSlice
): RoomSlice {
  const priorFeed = prevSlice.activityFeed ?? []
  const prev = prevSlice.rawRound
  const next = reducedSlice.rawRound

  if (!next) {
    return { ...reducedSlice, activityFeed: priorFeed }
  }
  if (next.status === "RESOLVED") {
    return { ...reducedSlice, activityFeed: [] }
  }
  if (!prev || prev.roundId !== next.roundId) {
    const intro = waitingIntroItem(next)
    return { ...reducedSlice, activityFeed: intro ? [intro] : [] }
  }
  if (next.version <= prev.version) {
    return { ...reducedSlice, activityFeed: priorFeed }
  }

  const diff = roundDiff(prev, next)
  const derived = deriveBubbleEventsFromDiff(prev, next, diff)
  if (derived.length === 0) {
    return { ...reducedSlice, activityFeed: priorFeed }
  }

  const v = next.version
  const appended = derived.map((payload, i) => ({
    id: `${v}-${i}`,
    payload,
  }))
  const activityFeed = [...priorFeed, ...appended].slice(-ACTIVITY_FEED_MAX)
  return { ...reducedSlice, activityFeed }
}
